import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    catchError,
    combineLatest,
    concatMap,
    exhaustMap,
    map,
    mergeMap,
    of,
    switchMap,
    filter,
    withLatestFrom,
    from,
    EMPTY,
} from 'rxjs';
import { ModalWindowsService } from '../../../modals/services/modal-windows.service';
import { ProfileTeamsService } from '../../../profile/services/profile-teams.service';
import { SnackBarType } from '../../../shared/components/snack-bar/snack-bar.component';
import { NOTIFICATIONS } from '../../../shared/constants';
import { BadResponseWithError } from '../../../shared/models/bad-response-with-error.interface';
import { NotificationsService } from '../../../shared/services/notifications.service';
import { CommonSettingsActions } from '../../../shared/store/actions/actions-type.action';
import { ProfileTeam } from '../../../team/models/team.interface';
import {
    DistributedCustomReward,
    RewardSetting,
    TournamentSupervizersInput,
    TournamentQualifiersLeaderboardParticipant,
    TournamentStageSaveInput,
    TournamentStageLevelSaveInput,
    TournamentRoundSaveInput,
} from '../../models/api/requests/tournamentGeneral.request';
import {
    BadTournamentUpdateResponse,
    ErrorCodesToUpdateState,
    GetBroadcastingServiceResponse,
    TournamentResult,
    UpdateTournamentStateResponse,
} from '../../models/api/responses/tournamentGeneral.response';
import {
    TournamentRequestState,
    TournamentState,
} from '../../models/enums/tournaments.enum';
import { Tournament } from '../../models/tournaments.interface';
import { TournamentGeneralService } from '../../services/tournament-general.service';
import { TournamentGeneralActions, TournamentBracketsActions } from '../actions/actions-type.action';
import { PermissionsService } from 'src/app/permissions/services/permissions.service';
import { PermissionGroup } from 'src/app/permissions/models/requests/finalized-permissions.request';
import { GROUP_TYPES } from 'src/app/permissions/models/enums/permission-names';
import { TournamentBracketsService } from '../../services/tournament-brackets.service';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/shared/store/appState';
import { tournamentFeatureSelector, selectCurrentTournamentId } from '../selectors/tournamentGeneral.selector';

@Injectable()
export class TournamentGeneralEffect {
    private actions$ = inject(Actions);
    private tournamentGeneralService = inject(TournamentGeneralService);
    private notificationsService = inject(NotificationsService);
    private profileTeamsService = inject(ProfileTeamsService);
    private modalService = inject(ModalWindowsService);
    private permissionsService = inject(PermissionsService);
    private bracketsService = inject(TournamentBracketsService);
    private store = inject<Store<AppState>>(Store);
    // Track dispatched project fetches to avoid duplicates (during V2 load race conditions)
    private recentProjectFetchIds = new Set<string>();

    getTournamentById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.getTournamentById),
            switchMap((action) =>
                this.tournamentGeneralService
                    .getTournamentById(action.payload)
                    .pipe(
                        map((response: Tournament) =>
                            TournamentGeneralActions.getTournamentByIdSuccess({ payload: response })
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentGeneralActions.getTournamentByIdFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    fetchTournamentProject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.fetchTournamentProject),
            switchMap(({ payload: { tournamentProjectId } }) =>
                this.tournamentGeneralService.getTournamentProject(tournamentProjectId).pipe(
                    map((response: any) =>
                        TournamentGeneralActions.fetchTournamentProjectSuccess({ payload: response })
                    ),
                    catchError((response: BadResponseWithError) =>
                        of(TournamentGeneralActions.fetchTournamentProjectFailure({ payload: response }))
                    )
                )
            )
        )
    );

    triggerInitialStageInfo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.fetchTournamentProjectSuccess),
            map(({ payload }) => {
                const levels: any[] = Array.isArray(payload?.stageLevels) ? payload.stageLevels : [];
                const firstStageId: string | null = levels?.[0]?.stages?.[0]?.id ?? null;
                return firstStageId;
            }),
            filter((stageId: string | null): stageId is string => !!stageId),
            map((stageId: string) => TournamentGeneralActions.fetchStageInfo({ stageId }))
        )
    );

    populateScoringProjectsFromProject$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.fetchTournamentProjectSuccess),
            mergeMap(({ payload }) => {
                const actions: any[] = [];
                const levels: any[] = Array.isArray(payload?.stageLevels) ? payload.stageLevels : [];
                levels.forEach((lvl: any) => {
                    const stages: any[] = Array.isArray(lvl?.stages) ? lvl.stages : [];
                    stages.forEach((s: any) => {
                        const stageId: string | null = s?.id ?? null;
                        if (!stageId) return;
                        const scoringProjectId: string | null = s?.scoringProject?.id ?? s?.scoringProjectId ?? null;
                        const scoringProjectObj: any | null = s?.scoringProject ?? (scoringProjectId ? { id: scoringProjectId } : null);
                        if (scoringProjectObj) {
                            actions.push(
                                TournamentGeneralActions.setStageScoringProject({
                                    stageId,
                                    scoringProject: scoringProjectObj,
                                })
                            );
                        }
                    });
                });
                return actions;
            })
        )
    );

    updateTournamentState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.updateTournamentState),
            exhaustMap(({ payload: { id, state, depsState, sideEffect, useV2Base } }) => {
                const depsRequests =
                    depsState?.map((dState) =>
                        this.tournamentGeneralService.updateTournamentState({
                            id,
                            state: dState,
                            useV2Base,
                        })
                    ) ?? [];

                return combineLatest([
                    of(id),
                    of(state),
                    of(sideEffect),
                    of(useV2Base),
                    ...depsRequests,
                ]);
            }),
            exhaustMap(([id, state, sideEffect, useV2Base, ...requests]) =>
                this.tournamentGeneralService
                    .updateTournamentState({ id, state, useV2Base })
                    .pipe(
                        map((response: UpdateTournamentStateResponse) => {
                            this.notificationsService.openSnackBar({
                                title:
                                    state === TournamentRequestState.Delete
                                        ? NOTIFICATIONS.TOURNAMENT_DELETED
                                        : NOTIFICATIONS.TOURNAMENT_DRAFT_SAVED,
                                type: SnackBarType.Success,
                            });

                            if (sideEffect) {
                                sideEffect();
                            }

                            return TournamentGeneralActions.updateTournamentStateSuccess(
                                {
                                    payload: response,
                                }
                            );
                        }),
                        catchError((response: BadTournamentUpdateResponse) => {
                            const errorTitle = response?.error?.message;
                            const msg =
                                response?.error?.failures?.[0]?.errorMessage;

                            if (
                                response.error.code ===
                                ErrorCodesToUpdateState.InsufficientAccountBalance
                            ) {
                                this.modalService.openNotEnoughBalanceModal();
                            } else {
                                this.notificationsService.openSnackBar({
                                    title: errorTitle,
                                    message: msg,
                                    type: SnackBarType.Error,
                                    error: response.error,
                                });
                            }

                            if (
                                response.error.code ===
                                ErrorCodesToUpdateState.TournamentCanceledDueToNoQuorum
                            ) {
                                return of(
                                    TournamentGeneralActions.updateTournamentStateFailure(
                                        {
                                            payload: response,
                                            state: TournamentState.CanceledDueToNoQuorum,
                                        }
                                    )
                                );
                            }

                            return of(
                                TournamentGeneralActions.updateTournamentStateFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    distributePlayers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.distributePlayers),
            exhaustMap(({ payload }) =>
                this.tournamentGeneralService
                    .distributePlayers(payload.tournamentId, payload.request)
                    .pipe(
                        map(() => {
                            this.modalService.closeDistributePlayersModal();
                            return TournamentGeneralActions.distributePlayersSuccess(
                                {
                                    payload: payload.request,
                                }
                            );
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                type: SnackBarType.Error,
                                message: response.error.message,
                                error: response.error,
                            });
                            return of(
                                TournamentGeneralActions.distributePlayersFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    updateTournamentScoring$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.updateTournamentScoring),
            exhaustMap(({ payload }) =>
                this.tournamentGeneralService
                    .updateTournamentScoringSettings(
                        payload.tournamentId,
                        payload.request
                    )
                    .pipe(
                        map((response) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.TOURNAMENT_SCORING_SAVED,
                                type: SnackBarType.Success,
                            });
                            return TournamentGeneralActions.updateTournamentScoringSuccess(
                                {
                                    payload: response,
                                }
                            );
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                type: SnackBarType.Error,
                                message: response.error.message,
                                error: response.error,
                            });

                            return of(
                                TournamentGeneralActions.updateTournamentScoringFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    getBroadcastingServices$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.getBroadcastingServices),
            switchMap((action) =>
                this.tournamentGeneralService.getBroadcastingServices().pipe(
                    map((response: GetBroadcastingServiceResponse[]) =>
                        TournamentGeneralActions.getBroadcastingServicesSuccess(
                            {
                                payload: response,
                            }
                        )
                    ),
                    catchError((response: BadResponseWithError) =>
                        of(
                            TournamentGeneralActions.getBroadcastingServicesFailure(
                                {
                                    payload: response,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    fetchTournamentResult$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.fetchTournamentResults),
            switchMap((action) =>
                this.tournamentGeneralService
                    .fetchTournamentResults(action.payload.tournamentId)
                    .pipe(
                        map((response: TournamentResult[]) =>
                            TournamentGeneralActions.fetchTournamentResultsSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentGeneralActions.fetchTournamentResultsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    fetchTournamentResultV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.fetchTournamentResultsV2),
            switchMap((action) =>
                this.tournamentGeneralService
                    .fetchTournamentResultsV2(action.payload.tournamentId)
                    .pipe(
                        map((response: TournamentResult[]) =>
                            TournamentGeneralActions.fetchTournamentResultsV2Success({
                                payload: response,
                            })
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentGeneralActions.fetchTournamentResultsV2Failure({
                                    payload: response,
                                })
                            )
                        )
                    )
            )
        )
    );

    addTournamentBroadcastingServices$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.addTournamentBroadcastingServices),
            switchMap((action) =>
                this.tournamentGeneralService
                    .addTournamentBroadcastingServices(
                        action.payload.tournamentId,
                        action.payload.request
                    )
                    .pipe(
                        map((response: Tournament) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.TOURNAMENT_DRAFT_SAVED,
                                type: SnackBarType.Success,
                            });

                            return TournamentGeneralActions.addTournamentBroadcastingServicesSuccess(
                                {
                                    payload: response,
                                }
                            );
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                type: SnackBarType.Error,
                                message: response.error.message,
                                error: response.error,
                            });

                            return of(
                                TournamentGeneralActions.addTournamentBroadcastingServicesFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    updateTournamentBroadcastingServices$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                TournamentGeneralActions.updateTournamentBroadcastingServices
            ),
            switchMap((action) =>
                this.tournamentGeneralService
                    .updateTournamentBroadcastingServices(
                        action.payload.tournamentId,
                        action.payload.request
                    )
                    .pipe(
                        map((response: Tournament) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.TOURNAMENT_DRAFT_SAVED,
                                type: SnackBarType.Success,
                            });

                            return TournamentGeneralActions.updateTournamentBroadcastingServicesSuccess(
                                {
                                    payload: response,
                                }
                            );
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                type: SnackBarType.Error,
                                message: response.error.message,
                                error: response.error,
                            });

                            return of(
                                TournamentGeneralActions.updateTournamentBroadcastingServicesFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    fetchTournamentRewardsSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.fetchTournamentRewardsSettings),
            switchMap((action) =>
                this.tournamentGeneralService
                    .fetchTournamentRewardsSettings(
                        action.payload.tournamentId,
                        action.payload.useV2Base
                    )
                    .pipe(
                        map((response: RewardSetting[]) =>
                            TournamentGeneralActions.fetchTournamentRewardsSettingsSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentGeneralActions.fetchTournamentRewardsSettingsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    saveTournamentRewardsSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.saveTournamentRewardsSettings),
            switchMap((action) =>
                this.tournamentGeneralService
                    .saveTournamentRewardsSettings(
                        action.payload.tournamentId,
                        action.payload.request,
                        action.payload.useV2Base
                    )
                    .pipe(
                        mergeMap((response: RewardSetting[]) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.TOURNAMENT_DRAFT_SAVED,
                                type: SnackBarType.Success,
                            });

                            const sideEffects: any[] = [];
                            if (action.payload.navigateToSettings) {
                                if(action.payload.useV2Base){
                                    sideEffects.push(
                                        CommonSettingsActions.navigateToTournamentSettingsV2(
                                            {
                                                payload: {
                                                    tournamentId:
                                                    action.payload.tournamentId,
                                                },
                                            }
                                        )
                                    );
                                }else {

                                sideEffects.push(
                                    CommonSettingsActions.navigateToTournamentSettings(
                                        {
                                            payload: {
                                                tournamentId:
                                                    action.payload.tournamentId,
                                            },
                                        }
                                    )
                                );
                                }

                            }

                            return [
                                TournamentGeneralActions.saveTournamentRewardsSettingsSuccess(
                                    {
                                        payload:
                                            action.payload.request
                                                .rewardSettings,
                                    }
                                ),
                                ...sideEffects,
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                type: SnackBarType.Error,
                                message: response.error.message,
                                error: response.error,
                            });

                            return of(
                                TournamentGeneralActions.saveTournamentRewardsSettingsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    fetchDistributedCustomRewards$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.fetchDistributedCustomRewards),
            switchMap((action) =>
                this.tournamentGeneralService
                    .fetchDistributedCustomRewards(action.payload.tournamentId)
                    .pipe(
                        map((response: DistributedCustomReward[]) =>
                            TournamentGeneralActions.fetchDistributedCustomRewardsSuccess(
                                {
                                    payload: response,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentGeneralActions.fetchDistributedCustomRewardsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    saveDistributedCustomRewards$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.saveDistributedCustomRewards),
            switchMap((action) =>
                this.tournamentGeneralService
                    .saveDistributedCustomRewards(
                        action.payload.tournamentId,
                        action.payload.request
                    )
                    .pipe(
                        concatMap((response: DistributedCustomReward[]) => {
                            this.modalService.closeDistributeCustomRewardsModal();

                            return [
                                TournamentGeneralActions.saveDistributedCustomRewardsSuccess(
                                    {
                                        payload: response,
                                    }
                                ),
                                TournamentGeneralActions.getTournamentById({
                                    payload: action.payload.tournamentId,
                                }),
                                TournamentGeneralActions.fetchDistributedCustomRewards(
                                    {
                                        payload: {
                                            tournamentId:
                                                action.payload.tournamentId,
                                        },
                                    }
                                ),
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                type: SnackBarType.Error,
                                message: response.error.message,
                                error: response.error,
                            });

                            return of(
                                TournamentGeneralActions.saveDistributedCustomRewardsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    fetchOrganizersTeams$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.fetchTournamentOrganizersTeams),
            switchMap((action) =>
                this.profileTeamsService
                    .getAllTeams(action.payload.profileId)
                    .pipe(
                        map((teams: ProfileTeam[]) =>
                            TournamentGeneralActions.fetchTournamentOrganizersTeamsSuccess(
                                {
                                    payload: { teams },
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentGeneralActions.fetchTournamentOrganizersTeamsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    fetchTournamentQualifiersLeaderboard$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.fetchTournamentQualifiersLeaderboard),
            switchMap((action) =>
                this.tournamentGeneralService
                    .fetchTournamentQualifiersLeaderboard(action.payload)
                    .pipe(
                        map((response: TournamentQualifiersLeaderboardParticipant[]) =>
                            TournamentGeneralActions.fetchTournamentQualifiersLeaderboardSuccess({
                                payload: response,
                            })
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentGeneralActions.fetchTournamentQualifiersLeaderboardFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    saveTournamentSupervizers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.saveTournamentSupervizers),
            switchMap((action) =>
                this.tournamentGeneralService
                    .saveTournamentSupervizers(action.payload.tournamentId, action.payload.input)
                    .pipe(
                        map((response: TournamentSupervizersInput) => {
                                this.notificationsService.openSnackBar({
                                    title: NOTIFICATIONS.TOURNAMENT_STAFF_SAVED,
                                    type: SnackBarType.Success,
                                });

                                return TournamentGeneralActions.saveTournamentSupervizersSuccess({
                                payload: response,
                            })
                        }),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentGeneralActions.saveTournamentSupervizersFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    fetchTournamentSupervizers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.fetchTournamentSupervizers),
            switchMap((action) =>
                this.permissionsService
                    .fetchGroup(action.payload.name, action.payload.contextEntityType, action.payload.contextEntityId)
                    .pipe(
                        mergeMap((response: PermissionGroup) =>
                            this.permissionsService.getUsersByGroup(response.groupId),
                            (_, supervisors) => {
                                    return (action.payload.name == GROUP_TYPES.Moderator) ?
                                    TournamentGeneralActions.fetchTournamentSupervizersSuccess({
                                        payload: {
                                           ids: supervisors.items.map(x => x.userId),
                                           name: action.payload.name
                                        }
                                    }) :
                                    TournamentGeneralActions.fetchTournamentOperatorSuccess({
                                        payload: {
                                           ids: supervisors.items.map(x => x.userId),
                                           name: action.payload.name
                                        }
                                    })
                                }
                            ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentGeneralActions.fetchTournamentSupervizersFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );
    getTournamentByIdV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.getTournamentByIdV2),
            switchMap((action) =>
                this.tournamentGeneralService
                    .getTournamentByIdV2(action.payload)
                    .pipe(
                        // Dispatch base tournament success and, if present, also fetch its project
                        mergeMap((response: Tournament) => {
                            const actions: any[] = [
                                // Signal-specific success for V2 flows
                                TournamentGeneralActions.getTournamentByIdV2Success({ payload: response }),
                                // Store tournament payload (shared with V1)
                                TournamentGeneralActions.getTournamentByIdSuccess({ payload: response }),
                            ];
                            const projectId = (response as any)?.tournamentProjectId
                                || (response as any)?.tournamentProject?.id
                                || null;
                            if (projectId) {
                                // Mark as requested and dispatch builder project fetch
                                this.recentProjectFetchIds.add(projectId);
                                actions.push(
                                    TournamentGeneralActions.fetchTournamentProject({
                                        payload: { tournamentProjectId: projectId },
                                    })
                                );
                            }
                            return from(actions);
                        }),
                        catchError((response: BadResponseWithError) =>
                            of(
                                // We can also reuse the V1 failure action.
                                TournamentGeneralActions.getTournamentByIdFailure({
                                    payload: response,
                                })
                            )
                        )
            )
        )
    ));

    fetchBuilderOnV2Success$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.getTournamentByIdV2Success),
            withLatestFrom(this.store.select(tournamentFeatureSelector)),
            mergeMap(([{ payload }, state]) => {
                const projectId = (payload as any)?.tournamentProjectId
                    || (payload as any)?.tournamentProject?.id
                    || null;
                const existingProject = (state as any)?.general?.tournament?.tournamentProject;
                if (projectId && !existingProject && !this.recentProjectFetchIds.has(projectId)) {
                    this.recentProjectFetchIds.add(projectId);
                    return of(
                        TournamentGeneralActions.fetchTournamentProject({
                            payload: { tournamentProjectId: projectId },
                        })
                    );
                }
                return EMPTY;
            })
        )
    );

    saveTournamentProjectStage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.saveTournamentProjectStage),
            switchMap((action) =>
                this.tournamentGeneralService
                    .saveTournamentProjectStage(action.payload)
                    .pipe(
                        map((response: TournamentStageSaveInput) => {
                                this.notificationsService.openSnackBar({
                                    title: NOTIFICATIONS.TOURNAMENT_STAGE_SAVED,
                                    type: SnackBarType.Success,
                                });

                                return TournamentGeneralActions.saveTournamentProjectStageSuccess({
                                payload: response,
                            })
                        }),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentGeneralActions.saveTournamentProjectStageFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    saveTournamentProjectStageLevel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.saveTournamentProjectStageLevel),
            switchMap((action) =>
                this.tournamentGeneralService
                    .saveTournamentProjectStageLevel(action.payload)
                    .pipe(
                        map((response: TournamentStageLevelSaveInput) => {
                                this.notificationsService.openSnackBar({
                                    title: NOTIFICATIONS.TOURNAMENT_STAGE_LEVEL_SAVED,
                                    type: SnackBarType.Success,
                                });

                                return TournamentGeneralActions.saveTournamentProjectStageLevelSuccess({
                                payload: response,
                            })
                        }),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentGeneralActions.saveTournamentProjectStageLevelFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    // Fetch a stage info by id (getStageById) and cache scoringProject mapping
    fetchStageInfo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.fetchStageInfo),
            switchMap(({ stageId }) =>
                this.bracketsService.fetchStageById(stageId).pipe(
                    mergeMap((stage: any) => {
                        const scoringProjectId: string | null =
                            stage?.scoringProject?.id ?? stage?.scoringProjectId ?? null;
                        const scoringProjectObj: any | null =
                            stage?.scoringProject ?? (scoringProjectId ? { id: scoringProjectId } : null);
                        const actions: any[] = [
                            TournamentGeneralActions.fetchStageInfoSuccess({ payload: stage }),
                        ];
                        if (scoringProjectId) {
                            actions.push(
                                TournamentGeneralActions.setStageScoringProjectId({
                                    stageId,
                                    scoringProjectId,
                                })
                            );
                        }
                        if (scoringProjectObj) {
                            actions.push(
                                TournamentGeneralActions.setStageScoringProject({
                                    stageId,
                                    scoringProject: scoringProjectObj,
                                })
                            );
                        }
                        return actions;
                    }),
                    catchError((response: BadResponseWithError) =>
                        of(TournamentGeneralActions.fetchStageInfoFailure({ payload: response, stageId }))
                    )
                )
            )
        )
    );

    setRoundDataOnStageInfo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.fetchStageInfoSuccess),
            withLatestFrom(this.store.select(selectCurrentTournamentId)),
            switchMap(([{ payload: stage }, tournamentId]) => {
                const rounds: any[] = Array.isArray(stage?.rounds) ? stage.rounds : [];
                const active = rounds.find((r: any) => r?.active);
                const roundId: string | null = active?.id ?? rounds?.[0]?.id ?? null;
                if (tournamentId && roundId) {
                    return of(
                        TournamentBracketsActions.fetchRoundDataV2({
                            payload: { tournamentId, roundId },
                        })
                    );
                }
                return EMPTY;
            })
        )
    );

    deleteTournamentProjectStageLevel$ = createEffect(() =>
            this.actions$.pipe(
                ofType(TournamentGeneralActions.deleteTournamentProjectStageLevel),
                exhaustMap((action) =>
                    this.tournamentGeneralService
                        .deleteTournamentProjectStageLevel(action.id)
                        .pipe(
                            map(() =>
                                TournamentGeneralActions.deleteTournamentProjectStageLevelSuccess(action)
                            ),
                            catchError((response: BadResponseWithError) => {
                                this.notificationsService.openSnackBar({
                                    title: 'Error',
                                    message: response.error.message,
                                    type: SnackBarType.Error,
                                    error: response.error,
                                });
                                return of(
                                    TournamentGeneralActions.deleteTournamentProjectStageLevelFailure(action)
                                );
                            })
                        )
                )
            )
        );

    deleteTournamentProjectStage$ = createEffect(() =>
            this.actions$.pipe(
                ofType(TournamentGeneralActions.deleteTournamentProjectStage),
                exhaustMap((action) =>
                    this.tournamentGeneralService
                        .deleteTournamentProjectStage(action.id)
                        .pipe(
                            map(() =>
                                TournamentGeneralActions.deleteTournamentProjectStageSuccess(action)
                            ),
                            catchError((response: BadResponseWithError) => {
                                this.notificationsService.openSnackBar({
                                    title: 'Error',
                                    message: response.error.message,
                                    type: SnackBarType.Error,
                                    error: response.error,
                                });
                                return of(
                                    TournamentGeneralActions.deleteTournamentProjectStageFailure(action),
                                );
                            })
                        )
                )
            )
        );

    saveTournamentProjectRound$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TournamentGeneralActions.saveTournamentProjectRound),
            switchMap((action) =>
                this.tournamentGeneralService
                    .saveTournamentProjectRound(action.stageId, action.payload, action.stageSchema)
                    .pipe(
                        map(() => {
                                this.notificationsService.openSnackBar({
                                    title: NOTIFICATIONS.TOURNAMENT_ROUND_SAVED,
                                    type: SnackBarType.Success,
                                });

                                return TournamentGeneralActions.saveTournamentProjectRoundSuccess({
                                payload: action.payload,
                            })
                        }),
                        catchError((response: BadResponseWithError) =>
                            of(
                                TournamentGeneralActions.saveTournamentProjectRoundFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    finalizeTournamentProjectStage$ = createEffect(() =>
            this.actions$.pipe(
                ofType(TournamentGeneralActions.finalizeTournamentProject),
                exhaustMap((payload) =>
                    this.tournamentGeneralService
                        .finalizeTournamentProject(payload.id)
                        .pipe(
                            map(() =>
                                TournamentGeneralActions.finalizeTournamentProjectSuccess(payload)
                            ),
                            catchError((response: BadResponseWithError) => {
                                this.notificationsService.openSnackBar({
                                    title: 'Error',
                                    message: response.error.message,
                                    type: SnackBarType.Error,
                                    error: response.error,
                                });
                                return of(
                                    TournamentGeneralActions.finalizeTournamentProjectFailure({
                                        payload: response,
                                    })
                                );
                            })
                        )
                )
            )
        );
}
